import React, { ReactNode } from 'react';
import Spinner from '../../../UIComponents/Spinner';
import cls from './DataTable.module.scss';

/** The admin-wide sort order: 0 ascending, 1 descending */
export type DataTableSort = {
  field: string;
  order: 0 | 1;
};

export type DataTableColumn<T> = {
  key: string;
  title: string;
  /** The header sorts by the column's key; needs the table's onSortChange */
  sortable?: boolean;
  /** Grid track for this column, e.g. "minmax(120px, 1fr)" */
  width?: string;
  /** Lets the cell show what leaves its box, e.g. an open dropdown menu */
  allowOverflow?: boolean;
  render: (item: T) => ReactNode;
};

type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  items: T[] | undefined;
  isLoading: boolean;
  emptyText: string;
  rowKey: (item: T) => string;
  onRowClick?: (item: T) => void;
  footer?: ReactNode;
  sort?: DataTableSort;
  /** Called with the key of the sortable column whose header was clicked */
  onSortChange?: (field: string) => void;
};

const sortMark = (order: 0 | 1) => (order === 0 ? ' ▲' : ' ▼');

const DataTable = <T,>(props: DataTableProps<T>) => {
  const { columns, items, isLoading, emptyText, rowKey, onRowClick, footer, sort, onSortChange } =
    props;
  const gridTemplateColumns = columns
    .map((column) => column.width ?? 'minmax(120px, 1fr)')
    .join(' ');
  if (isLoading) {
    return (
      <div className={cls.TableContainer}>
        <div className={cls.Placeholder}>
          <Spinner />
        </div>
      </div>
    );
  }
  // the footer carries the pagination, so an empty page still lets you walk back
  if (!items || items.length === 0) {
    return (
      <div className={cls.TableContainer}>
        <div className={cls.Placeholder}>{emptyText}</div>
        {footer && <div className={cls.Footer}>{footer}</div>}
      </div>
    );
  }
  return (
    <div className={cls.TableContainer}>
      <div className={cls.TableHeader} style={{ gridTemplateColumns }} data-testid="table-header">
        {columns.map((column) => (
          <div key={column.key} className={cls.HeaderCell}>
            {column.sortable && onSortChange ? (
              <button
                type="button"
                className={cls.SortButton}
                onClick={() => onSortChange(column.key)}
              >
                {column.title}
                {sort?.field === column.key && sortMark(sort.order)}
              </button>
            ) : (
              column.title
            )}
          </div>
        ))}
      </div>
      <div className={cls.TableBody}>
        {items.map((item) => (
          <div
            key={rowKey(item)}
            className={onRowClick ? `${cls.TableRow} ${cls.ClickableRow}` : cls.TableRow}
            style={{ gridTemplateColumns }}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
            data-testid="table-row"
          >
            {columns.map((column) => (
              <div
                key={column.key}
                className={
                  column.allowOverflow ? `${cls.TableCell} ${cls.OverflowCell}` : cls.TableCell
                }
              >
                {column.render(item)}
              </div>
            ))}
          </div>
        ))}
      </div>
      {footer && <div className={cls.Footer}>{footer}</div>}
    </div>
  );
};

export default DataTable;
